import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

export type YoyDeltaCellKind = 'up' | 'down' | 'flat' | 'na';

/**
 * Shared YOY delta cell — renders the loading spinner, an empty slot, or the colour-coded
 * `↑X.X%` / `↓X.X%` / `–0%` / `▵n/a` label with arrow + value split for vertical centring.
 *
 * Used in stats-table metric cells (Portfolio / behavior-trends) and inline in funnel-chart
 * bar labels (mini-bar-chart-html) via the `inline` flag, which swaps `.ft-yoy-delta` for
 * `.ft-yoy-delta.-inline` so the same SCSS in `_yoy-delta.scss` styles both layouts.
 */
@Component({
    selector: 'ft-yoy-delta',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './yoy-delta.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class YoyDeltaComponent {
    /** Pre-formatted display string (e.g. `↑4.1%`, `▵n/a`). Empty / null → nothing rendered. */
    @Input() label: string | null = '';
    /** Drives the colour class — `up` green, `down` red, `flat` / `na` grey. */
    @Input() kind: YoyDeltaCellKind = 'na';
    /** True while prior data is in flight — renders the mini-spinner instead of the label. */
    @Input() loading = false;
    /** Inline variant — delta sits to the RIGHT of a value on the same line (funnel bars).
     * Off by default = stacked variant (delta line UNDER the value, as in stats-table cells). */
    @Input() inline = false;
}
